import React from 'react';
import {
    ButtonGroup,
    DrawerBody,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    Flex,
    VStack,
    Box,
} from '@cad-arc';
import { AttributeOption, ConsolidatedLocationView, FormResource } from '@mark43/cad-api';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useApiClient } from '~/api-client';
import { useAttributesByType } from '../../attributes/use-attributes-by-type';
import { toAttributeOption } from '../../attributes/to-attribute-option';
import { SpecialAssignmentRequest } from '../types';
import { IntegratedFormProvider } from '../../forms/integrated-form-provider';
import { FormBody } from '../../forms/integrated-form-components/form-body';
import { FormSaveButton } from '../../forms/integrated-form-components/form-save-button';
import { FormCancelButton } from '../../forms/integrated-form-components/form-cancel-button';
import { FormAttributeSelect } from '../../forms/integrated-form-controls/form-attribute-select';
import { FormLocationSelect } from '../../forms/integrated-form-controls/form-location-select';
import { FormInput } from '../../forms/integrated-form-controls/form-input';
import { FormCheckbox } from '../../forms/integrated-form-controls/form-checkbox';
import { FormTextarea } from '../../forms/integrated-form-controls/form-text-area';
import { useLocationApi } from '../../location-select/hooks/use-location-api';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { useApplicationType } from '../../common/hooks/use-application-type';

type FormType = {
    assignmentType: AttributeOption | undefined;
    consolidatedLocationView: ConsolidatedLocationView | undefined;
    subPremise: string | undefined;
    updateUnitLocation: boolean;
    comments: string;
};

export const SpecialAssignmentDrawerContent: React.FC<{
    unitId?: number;
    statusId?: number;
    closeDrawer: () => void;
}> = ({ unitId, statusId, closeDrawer }) => {
    const apiClient = useApiClient();
    const { payForEsriLocation } = useLocationApi();
    const { attributes: specialAssignmentAttributes } = useAttributesByType({
        attributeType: 'SPECIAL_ASSIGNMENT',
    });
    const { subpremiseSupport, firstResponderCanEditUnitLocations } = useEnabledFeatures();
    const { isFirstResponder } = useApplicationType();

    const defaultSpecialAssignmentAttribute = specialAssignmentAttributes.find(
        (attr) => attr.isDefault
    );

    const allowedToUpdateUnitLocation = !isFirstResponder || firstResponderCanEditUnitLocations;

    const mutation = useMutation(
        ({
            specialAssignmentView,
            updateUnitLocation,
        }: {
            specialAssignmentView: SpecialAssignmentRequest;
            updateUnitLocation: boolean;
        }) => {
            return apiClient.request<FormResource.UpsertSpecialAssignment>({
                method: 'POST',
                url: 'form/special_assignment/',
                params: {
                    update_unit_location: updateUnitLocation,
                },
                // @ts-expect-error not including versioning fields
                data: specialAssignmentView,
            });
        },
        {
            onSuccess() {
                closeDrawer();
            },
        }
    );

    const form = useForm<FormType>({
        defaultValues: {
            assignmentType: undefined,
            consolidatedLocationView: undefined,
            subPremise: undefined,
            updateUnitLocation: allowedToUpdateUnitLocation,
            comments: '',
        },
        mode: 'onBlur',
    });

    useEffect(() => {
        const value = defaultSpecialAssignmentAttribute
            ? toAttributeOption(defaultSpecialAssignmentAttribute)
            : undefined;
        form.setValue('assignmentType', value);
    }, [form, defaultSpecialAssignmentAttribute]);

    const onSubmit = React.useCallback(
        function onSubmit(data: FormType): void {
            if (!unitId || !data.assignmentType) {
                return;
            }
            if (form.getFieldState('consolidatedLocationView').isDirty) {
                const location = form.getValues('consolidatedLocationView');
                if (location !== undefined) {
                    payForEsriLocation(location);
                }
            }
            return mutation.mutate({
                specialAssignmentView: {
                    unitIds: [unitId],
                    unitId,
                    specialAssignmentTypeAttrId: data.assignmentType.value,
                    location: data.consolidatedLocationView,
                    comments: data.comments,
                },
                updateUnitLocation: data.updateUnitLocation,
            });
        },
        [form, mutation, payForEsriLocation, unitId]
    );

    if (!unitId || !statusId) {
        return (
            <DrawerContent>
                <DrawerHeader>Special Assignment</DrawerHeader>
            </DrawerContent>
        );
    }

    return (
        <IntegratedFormProvider mutation={mutation} form={form} onSubmit={onSubmit}>
            <DrawerContent>
                <DrawerHeader>Special Assignment</DrawerHeader>
                <DrawerBody>
                    <FormBody>
                        <VStack spacing={4} align="left">
                            <FormAttributeSelect
                                autoFocus={true}
                                name="assignmentType"
                                label="Assignment Type"
                                rules={{ required: 'Required' }}
                                control={form.control}
                                attributeType="SPECIAL_ASSIGNMENT"
                            />
                            <FormLocationSelect
                                name="consolidatedLocationView"
                                control={form.control}
                                label="Location"
                            />
                            {!subpremiseSupport && (
                                <Flex direction="row" mt={2}>
                                    <Box mr={2}>
                                        <FormInput
                                            name="subPremise"
                                            control={form.control}
                                            placeholder="Apt, Unit"
                                            inputProps={{
                                                maxLength: 200,
                                            }}
                                        />
                                    </Box>
                                </Flex>
                            )}
                            {allowedToUpdateUnitLocation && (
                                <FormCheckbox
                                    name="updateUnitLocation"
                                    control={form.control}
                                    aria-label="Update Unit Location"
                                    label="Update Unit Location"
                                />
                            )}
                            <FormTextarea
                                name="comments"
                                rules={{ required: 'Required' }}
                                control={form.control}
                                label="Comments"
                            />
                        </VStack>
                    </FormBody>
                </DrawerBody>
                <DrawerFooter>
                    <ButtonGroup>
                        <FormCancelButton onClick={closeDrawer} />
                        <FormSaveButton />
                    </ButtonGroup>
                </DrawerFooter>
            </DrawerContent>
        </IntegratedFormProvider>
    );
};
